import {
  cameraPosition,
  cos,
  float,
  Fn,
  hash,
  mat3,
  modelWorldMatrix,
  sin,
  vec3,
  type ShaderNodeObject,
} from 'three/tsl';
import type { Node } from 'three/webgpu';

const billboardPositionNode = /*@__PURE__*/ Fn(
  ([position]: [ShaderNodeObject<Node>]) => {
    // 1. Get the object's center position in World Space
    // The position is stored in the last column (index 3) of the modelWorldMatrix.
    const objectCenterWorld = modelWorldMatrix.element(float(3.0)).xyz;

    // 2. Calculate the direction from the object to the camera
    const toCamera = cameraPosition.sub(objectCenterWorld).toVar();

    // 3. Implement Y-Axis Billboarding:
    // This rotation keeps the object upright. We zero out the Y component of the look vector
    // to prevent tilting up/down, forcing rotation only around the Y-axis.
    toCamera.assign(
      vec3(toCamera.x, toCamera.y, toCamera.z).normalize() // Set Y to 0 for upright-only rotation
    );

    // Define the world's Up vector (Y-axis)
    const up = vec3(0, 1, 0).toVar();

    // Calculate the Right vector (perpendicular to Up and Look)
    const right = up.cross(toCamera).normalize();

    // Recalculate the true Up vector to ensure orthogonality
    up.assign(toCamera.cross(right).normalize());

    // 4. Create the new rotation matrix (R)
    // The columns of the matrix are the Right, Up, and Look vectors.

    const rotationMatrix = mat3(right, up, toCamera);

    // 5. Apply the rotation to the local vertex position and add the world center offset
    const rotatedLocalPosition = rotationMatrix.mul(position);

    // The final world position is the object's center plus the rotated local offset
    return objectCenterWorld.add(rotatedLocalPosition);
  }
);
const lookat = /*@__PURE__*/ Fn(
  ([position, target]: [ShaderNodeObject<Node>, ShaderNodeObject<Node>]) => {
    const localUp = vec3(0, 1, 0);
    const forward = target.sub(position).normalize();
    const right = forward.cross(localUp).normalize();
    const up = right.cross(forward).normalize();
    //@ts-expect-error issue with mat3 type
    const rotation = mat3(right, up, forward); // the order is important

    return rotation;
  }
);

const randomRotationMatrix = /*@__PURE__*/ Fn(
  ([position, index, time]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    // Create unique random rotation per instance using hash with instanceIndex
    // Use different seeds for each axis to get varied rotation
    const rotX = hash(index.mul(12.9898)).mul(10.0).add(time.mul(0.5));
    const rotY = hash(index.mul(78.233)).mul(10.0).add(time.mul(0.7));
    const rotZ = hash(index.mul(43.758)).mul(10.0).add(time.mul(0.3));

    // Build rotation matrix from euler angles (per instance)
    const cosX = cos(rotX);
    const sinX = sin(rotX);
    const cosY = cos(rotY);
    const sinY = sin(rotY);
    const cosZ = cos(rotZ);
    const sinZ = sin(rotZ);

    // Combined rotation matrix (Z * Y * X)
    const r00 = cosY.mul(cosZ);
    const r01 = cosY.mul(sinZ);
    const r02 = sinY.negate();
    const r10 = sinX.mul(sinY).mul(cosZ).sub(cosX.mul(sinZ));
    const r11 = sinX.mul(sinY).mul(sinZ).add(cosX.mul(cosZ));
    const r12 = sinX.mul(cosY);
    const r20 = cosX.mul(sinY).mul(cosZ).add(sinX.mul(sinZ));
    const r21 = cosX.mul(sinY).mul(sinZ).sub(sinX.mul(cosZ));
    const r22 = cosX.mul(cosY);

    const rotationMatrix = mat3(r00, r01, r02, r10, r11, r12, r20, r21, r22);

    // Apply rotation to local position
    const rotatedLocal = rotationMatrix.mul(position.xyz);

    return rotatedLocal;
  }
);

const randomRTColorBased = Fn(
  ([position, index, time]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    const randomTarget = vec3(
      hash(index.mul(12.9898)).sub(0.5),
      hash(index.mul(78.233)).sub(0.5),
      hash(index.mul(43.758)).sub(0.5)
    ).normalize();

    const animatedTarget = randomTarget
      .add(
        vec3(
          sin(time.mul(hash(index.mul(31.415)))),
          cos(time.mul(hash(index.mul(62.831)))),
          sin(time.mul(hash(index.mul(94.247))))
        )
      )
      .normalize();

    const rotationMatrix = lookat(
      vec3(0, 0, 0), // Look from origin
      animatedTarget // To unique target per instance
    );

    return rotationMatrix.mul(position.xyz);
  }
);

export {
  billboardPositionNode,
  lookat,
  randomRotationMatrix,
  randomRTColorBased,
};
